import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated } from '@/core/auth/SessionStore'
import type { PushSummary } from '@/core/sync/producaoSync'

interface State {
  savedCount?: number
  pushResult?: PushSummary | null
}

/**
 * Tela de sucesso após finalizar uma sessão (replica `SuccessScreen.kt`).
 *
 * Exibe contagem gravada + status da sincronização imediata:
 *  - se aceito tudo: "Sincronizado"
 *  - se push falhou (rede): "Aguardando sincronização"
 *  - se backend rejeitou alguns: lista os motivos
 */
export function ProducaoSuccessRoute() {
  const authed = useIsAuthenticated()
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state ?? {}) as State

  if (!authed) return <Navigate to="/login" replace />

  const saved = state.savedCount ?? 0
  const push = state.pushResult

  let statusLabel: string
  let statusTone: 'ok' | 'warn' | 'bad'
  let statusDetail: string | null = null

  if (push == null) {
    statusLabel = 'Aguardando sincronização'
    statusTone = 'warn'
  } else if (push.networkError) {
    statusLabel = 'Sem conexão — fila pendente'
    statusTone = 'warn'
    statusDetail = push.networkError
  } else if (push.rejected > 0) {
    statusLabel = `${push.accepted} aceito(s), ${push.rejected} rejeitado(s) pelo servidor`
    statusTone = 'bad'
    statusDetail = push.reasons.join(', ')
  } else {
    statusLabel = 'Sincronizado com o servidor'
    statusTone = 'ok'
  }

  return (
    <main className="min-h-svh bg-bg-base flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <CheckCircle2 className="h-20 w-20 text-ok mx-auto" />
        <h1 className="text-2xl font-bold text-on-surface mt-4">Sessão gravada</h1>
        <p className="text-on-surface-dim mt-2">
          {saved} apontamento(s) salvo(s) localmente.
        </p>

        <div
          className={
            statusTone === 'ok'
              ? 'mt-6 rounded-lg bg-ok/10 px-4 py-3 text-ok text-sm'
              : statusTone === 'warn'
                ? 'mt-6 rounded-lg bg-warn/10 px-4 py-3 text-warn text-sm'
                : 'mt-6 rounded-lg bg-bad/10 px-4 py-3 text-bad text-sm'
          }
        >
          <p className="font-medium">{statusLabel}</p>
          {statusDetail && <p className="text-xs mt-1 opacity-80">{statusDetail}</p>}
        </div>

        <Button
          variant="primary"
          size="lg"
          onClick={() => navigate('/producao', { replace: true })}
          className="w-full mt-8"
        >
          Ver sessões
        </Button>
      </div>
    </main>
  )
}
